import { useEffect, useRef, type ReactNode } from 'react';
import { usePrefersReducedMotion } from '../use-prefers-reduced-motion';

// px per ms — ~15px/s, slow enough to be a hint that the row continues rather than something moving
// under you while you read it.
export const DRIFT_PX_PER_MS = 0.015;
const DRIFT_RESUME_MS = 2500;

/**
 * One horizontally swipeable row that also drifts on its own, so it reads as continuing past the screen
 * edge rather than looking cut off.
 *
 * The drift is a `transform` animation on an inner track, NOT an animated `scrollLeft`. `scrollLeft`
 * cannot express this movement at all: the browser rounds a scroll offset to a whole CSS pixel
 * (`el.scrollLeft = 100.25` reads back as `100`), so at 15px/s the row could only ever jump one pixel
 * fifteen times a second — every frame in between showed no movement whatsoever, which is why the row
 * looked stuttery, and worse the higher the display's refresh rate: at 120Hz seven of every eight
 * frames were dead. A transform keeps its sub-pixel value and is interpolated by the compositor, so the
 * row moves on every frame the screen draws, whatever rate that is, and it does not compete with the
 * main thread — which on this page is busy rolling the live counters.
 *
 * The scroller itself is left entirely to the browser, which is what gives touch dragging, momentum,
 * snapping and the keyboard for free. The two only meet at the handover: a pointer press FOLDS the
 * drift into the real scroll position (cancel the animation, add the same amount to `scrollLeft`) so
 * the finger and the snap points act on where the tiles actually appear, and the drift restarts from
 * there a couple of seconds after the row goes quiet — rebuilt around the reader's new position, which
 * is what keeps it inside the row without ever clamping it.
 *
 * It ping-pongs rather than looping, which avoids duplicating every tile into the DOM, and it is paused
 * while the row is off screen.
 */
export function SwipeRow(props: { children: ReactNode; speed: number }) {
	const scroller = useRef<HTMLDivElement>(null);
	const track = useRef<HTMLDivElement>(null);
	const drift = useRef<Animation | null>(null);
	const restart = useRef<(() => void) | null>(null);
	const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const held = useRef(false);
	const onScreen = useRef(false);
	const reduce = usePrefersReducedMotion();

	/**
	 * Hands the drift's offset over to the scroller, so the row is where it looks like it is: the finger
	 * and the snap points act on the tiles' apparent position rather than on the one they were laid out
	 * at, and the drift can start again from zero.
	 */
	const fold = () => {
		const el = scroller.current;
		const inner = track.current;
		if (!el || !inner || !drift.current) return;
		const drifted = -new DOMMatrix(getComputedStyle(inner).transform).m41;
		// Cancelling drops the transform and the `scrollLeft` write puts it back as scroll: one task, so
		// the two never render apart. The write rounds to a whole pixel — the sub-pixel loss is the
		// browser's own limit and is exactly what the drift exists to avoid rendering with.
		drift.current.cancel();
		drift.current = null;
		el.scrollLeft += drifted;
	};

	const speed = props.speed;
	useEffect(() => {
		// No animation is created at all under reduced motion, rather than created and left paused.
		if (reduce) return;
		const el = scroller.current;
		const inner = track.current;
		if (!el || !inner) return;

		const start = () => {
			// Folding rather than cancelling: cancelling alone drops the transform and would snap the row
			// back to the scroll position the drift started from.
			fold();
			// Snapping belongs to finger-driven swipes only. Left on during the drift it does not merely
			// tug the row, it stops it dead: the tiles are snap AREAS, so a transform moves them, and the
			// browser re-snaps the scroller to follow — `scrollLeft` chases the transform frame by frame
			// and the two cancel out to a row that stands perfectly still. `hold` hands snapping back.
			el.style.scrollSnapType = 'none';
			// `offsetWidth` is the track's LAYOUT width, which its own transform does not change — reading
			// the scroller's `scrollWidth` instead would shrink as the drift advanced.
			const full = inner.offsetWidth - el.clientWidth;
			if (full <= 0 || held.current) return;
			// The drift always travels the WHOLE row, from wherever the reader left it: it may hand back the
			// distance already scrolled (a POSITIVE translate, which reveals the tiles behind) as well as
			// take up the room ahead. Giving it only the room ahead is what left it dead after a swipe —
			// someone who swiped to the last tile had no room ahead at all, so there was nothing to animate
			// and the row never moved again.
			const scrolled = el.scrollLeft;
			drift.current = inner.animate(
				{ transform: [`translate3d(${scrolled}px,0,0)`, `translate3d(${scrolled - full}px,0,0)`] },
				{ duration: full / speed, direction: 'alternate', iterations: Infinity, easing: 'linear' },
			);
			// Wound forward to where the row already is, so it picks up from the reader's own position
			// rather than jumping back to the first tile, and carries on towards the end.
			drift.current.currentTime = scrolled / speed;
			if (!onScreen.current) drift.current.pause();
		};
		restart.current = start;
		start();

		const visibility = new IntersectionObserver(([entry]) => {
			onScreen.current = entry?.isIntersecting ?? false;
			if (onScreen.current) drift.current?.play();
			else drift.current?.pause();
		});
		visibility.observe(el);

		// How far the drift may travel is a function of both widths, so a rotation or a font swap that
		// changes either has to rebuild the animation rather than keep animating to a stale distance.
		const resize = new ResizeObserver(start);
		resize.observe(el);
		resize.observe(inner);

		return () => {
			visibility.disconnect();
			resize.disconnect();
			drift.current?.cancel();
			drift.current = null;
			restart.current = null;
		};
	}, [reduce, speed]);

	/**
	 * Every scroll defers the restart too, not just the release, so a fling that is still travelling is
	 * never overtaken by the drift starting up again.
	 */
	const defer = () => {
		if (resumeTimer.current) clearTimeout(resumeTimer.current);
		resumeTimer.current = setTimeout(() => restart.current?.(), DRIFT_RESUME_MS);
	};

	/** Hands the row over to the reader: its own position, its own snapping, no drift pending. */
	const takeOver = () => {
		if (resumeTimer.current) clearTimeout(resumeTimer.current);
		fold();
		// Snapping back to CSS for the whole interaction, so the drag, its momentum and the tile it
		// settles on are all the browser's own.
		if (scroller.current) scroller.current.style.scrollSnapType = '';
	};

	const hold = () => {
		held.current = true;
		takeOver();
	};

	/**
	 * The release is listened for on the WINDOW, not on the row. `held` blocks the restart, so a release
	 * the row never hears is a row that never drifts again — and a swipe that ends with the pointer
	 * somewhere else (off the row, off the window) delivers its `pointerup` there, not here.
	 *
	 * `held` is cleared here and NOT in the scroll handler, which the fold's own `scrollLeft` write
	 * triggers a frame later: a finger resting still on the row would otherwise have the drift start up
	 * underneath it.
	 */
	useEffect(() => {
		const release = () => {
			if (!held.current) return;
			held.current = false;
			defer();
		};
		window.addEventListener('pointerup', release);
		window.addEventListener('pointercancel', release);
		return () => {
			window.removeEventListener('pointerup', release);
			window.removeEventListener('pointercancel', release);
			if (resumeTimer.current) clearTimeout(resumeTimer.current);
		};
	}, []);

	return (
		<div
			ref={scroller}
			// A wheel is the one way to scroll the row that sends no pointer event, so it has to fold the
			// drift itself before the scroll it is about to cause — and with no pointer down, there is no
			// release to wait for.
			onWheel={() => {
				takeOver();
				defer();
			}}
			onPointerDown={hold}
			onScroll={defer}
			className="hide-scrollbar snap-x [scroll-padding-left:1.5rem] overflow-x-auto"
		>
			{/* `w-max`, so the track is as wide as the tiles and the drift has a real width to read; left as
			    a plain block, the flex children would overflow a scroller-width box instead. */}
			<div ref={track} className="flex w-max gap-4 px-6">
				{props.children}
			</div>
		</div>
	);
}
